import type { ErrorComponentProps } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';

/** A screen file from before the latest update (it no longer exists once the system is updated). */
export const isStaleChunk = (e: unknown) => /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch dynamically/i.test(String((e as Error)?.message ?? e));

/** Reload once to pick up the new version; a second failure within a minute shows the screen instead of looping. */
export function reloadForUpdate(): boolean {
  try {
    const last = Number(sessionStorage.getItem('ruby.updateReload') ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem('ruby.updateReload', String(Date.now()));
  } catch {
    /* storage blocked: still reload once */
  }
  window.location.reload();
  return true;
}

/** Shown instead of a bare "Something went wrong": what happened and what to do, in plain words. */
export function ErrorScreen({ error, reset }: ErrorComponentProps) {
  const stale = isStaleChunk(error);
  const [reloading] = useState(() => stale && reloadForUpdate());
  const [details, setDetails] = useState(false);
  useEffect(() => console.error(error), [error]);
  if (reloading) return <p className="p-8 text-sm text-fg-muted">The system was just updated — loading the new version…</p>;
  return (
    <div className="mx-auto mt-16 max-w-lg rounded-xl border border-border bg-surface p-6 shadow-sm">
      <h1 className="text-lg font-semibold">{stale ? 'The system was updated' : 'This screen could not open'}</h1>
      <p className="mt-2 text-sm text-fg-muted">
        {stale
          ? 'A newer version was installed while this page was open. Reload to continue — anything already saved is safe.'
          : 'Something unexpected stopped this screen. Nothing you saved is lost. Reload the page; if it happens again, send the details below to the administrator.'}
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => window.location.reload()}><RefreshCw className="size-4" />Reload</Button>
        <Button variant="secondary" onClick={() => { reset(); window.history.back(); }}>Go back</Button>
        {!stale && <Button variant="ghost" onClick={() => setDetails((v) => !v)}>{details ? 'Hide details' : 'Details'}</Button>}
      </div>
      {details && <pre className="mt-4 max-h-48 overflow-auto rounded bg-surface-2 p-3 text-xs whitespace-pre-wrap">{String((error as Error)?.stack ?? (error as Error)?.message ?? error)}</pre>}
    </div>
  );
}
